import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import moment from 'moment';
import { useQuery } from '@tanstack/react-query';
import { Step, StepIcon, StepLabel, Stepper, Tab, Tabs } from '@mui/material';

import { encodeHasAssistanceParams } from '../../../utils/security.utils';
import usePushDataLayerEvent from '../../../utils/google-analytics.util';
import { logData } from '../../../utils/debug.util';
import { hideModal, setProvider, showModal } from '../../../store/slices/generalSlice';
import { RootState } from '../../../store/rootReducer';
import storageService from '../../../services/storage.service';
import { IMetadata } from '../../../models/metadata.model';
import useManageStorage from '../../../hooks/useManageStorage/useManageStorage';
import useAssistance from '../../../hooks/useAssistance/useAssistance';
import { ContentLayout, CustomTabs } from '../../../global-styles/layout';
import { theme } from '../../../global-styles/colors';
import { CRANE_LOCATION_STATE_CODES, INSURANCE_COMPANIES } from '../../../constants';
import { BodySmallText, Heading4Text } from '../../../components/typography/typography';
import Navbar from '../../../components/navbar/navbar';
import { ImbeeChatVariables } from '../../../components/imbee-chat/imbee-chat-utils';
import ImbeeChat from '../../../components/imbee-chat/ImbeeChat';
import ModalContainer from '../../../components/common/modal-container/modal-container';
import ClickToCall from '../../../components/click-to-call/ClickToCall';
import MediaNotification from '../../../components/assistance-notification/MediaNotification';
import AssistanceNotification from '../../../components/assistance-notification/AssistanceNotification';
import AlxButton from '../../../components/alx-components/button';
import { images } from '../../../assets/images/images';
import { CraneLocationParams, existInsuredReply } from '../../../api/assistance/assistance';
import { IServiceProps, CraneTrackingStep } from './service-types';
import { ServiceStyle } from './service-style';
import Provider from './provider/provider';
import MyCrane from './my-crane/my-crane';
import Data from './data/data';
import Chat from './chat/chat';

const CustomStepIcon = styled(StepIcon)`
  & text {
    display: none;
  }
  &.MuiStepIcon-root.Mui-active {
    color: ${(props) => props.theme.primary.primary50};
  }
  &.MuiStepIcon-root.Mui-completed {
    color: ${(props) => props.theme.primary.primary50};
  }
`;

const Service: React.FC<IServiceProps> = ({ showMenu = true }) => {
  const { t } = useTranslation(['common', 'auth', 'aia', 'assistance']);
  const [step, setStep] = useState<CraneTrackingStep>(1);
  const [valueTab, setValueTab] = useState(0);
  const dispatch = useDispatch();
  const modal = useSelector((state: RootState) => state.general.modal);
  const navigate = useNavigate();
  const didMountRef = useRef<boolean>(false);
  const { intervention, identifier } = useParams<{ intervention?: string; identifier?: string }>();
  const { cancelAssistance, requestIntervention } = useAssistance({ intervention });
  const { writeToStorage, readFromStorage, deleteFromStorage } = useManageStorage();
  const { pushDataLayerEvent } = usePushDataLayerEvent();
  const [loaded, setLoaded] = useState(false);
  const [chatVariables, setChatVariables] = useState<ImbeeChatVariables>([]);
  const [showMap, setShowMap] = useState<boolean>(false);

  useEffect(() => {
    const redirectTo = `/assistance/${intervention}/${identifier}`;
    storageService.setItem('assistanceUrl', redirectTo);
  }, [intervention, identifier]);

  const { data, isError } = requestIntervention;

  useEffect(() => {
    if (isError) {
      navigate('/invalid-data', { replace: true });
    }
  }, [isError, navigate]);

  const coordsMemoized: [number, number] | undefined = useMemo(
    () => (data?.latitud && data?.longitud ? [data.latitud, data.longitud] : undefined),
    [data?.latitud, data?.longitud],
  );

  let arrivalTime: string | undefined = undefined;
  if (data?.tiempoEstimado && +data?.tiempoEstimado) {
    arrivalTime = moment().add(+data?.tiempoEstimado, 'minutes').format('HH:mm');
  }

  const { data: replyData } = useQuery({
    queryKey: ['existInsuredReply', { intervention }],
    queryFn: () => {
      if (intervention) {
        return existInsuredReply(intervention);
      }
    },
    refetchOnWindowFocus: false,
    enabled: !!intervention,
    retry: false,
  });

  const steps = [
    t('aia:steps.starting-service'),
    t('aia:steps.crane-in-its-way'),
    t('aia:steps.crane-arriving-vhc'),
    t('aia:steps.vhc-delivered'),
  ];

  const handleChange = (event: React.ChangeEvent<any>, newValue: number) => {
    setValueTab(newValue);
  };

  const craneDataMemo: CraneLocationParams = useMemo(
    () => ({ identifier, file: data?.numeroExpediente, intervention, provider: data?.codigoProveedor }),
    [identifier, intervention, data],
  );

  const { requestCraneLocation } = useAssistance({ requestCraneLocationParams: craneDataMemo });
  const { data: craneDataResponse } = requestCraneLocation;

  const taxiCoordsMemoized: [number, number] | undefined = useMemo(() => {
    return craneDataResponse?.medio?.longitud && craneDataResponse?.medio?.latitud
      ? [craneDataResponse.medio.latitud, craneDataResponse.medio.longitud]
      : undefined;
  }, [craneDataResponse?.medio?.latitud, craneDataResponse?.medio?.longitud]);

  // ********** Manejo de flujo ********** */
  useEffect(() => {
    logData(`Y) Manejo de flujo - Estado de enlace: ${data?.estadoEnlace}`);
    logData(`Y) Manejo de flujo - Estado de asistencia: ${data?.estadoAsistencia}`);

    /* Enlace manipulado por el usuario - Envía a la pantalla de error */
    if (data?.estadoEnlace == 9) {
      navigate(`/invalid-data`);
      return;
    }

    /* Enlace caducado - Envía a la pantalla de enlace expirado */
    if (data && [2, 3].includes(data?.estadoEnlace)) {
      navigate('/expired-link');
      return;
    }

    switch (data?.estadoAsistencia) {
      case 1: // Asistencia activa
        setLoaded(true);
        break;
      case 2: {
        // Asistencia finalizada con menos de 15 días
        const reference = encodeHasAssistanceParams(data?.entidad, true);
        navigate(`/has-assistance/${reference}/${intervention}`);
        return;
      }
      case 3: // Asistencia finalizada con más de 15 días
        logData('Asistencia finalizada hace más de 15 días, redirigiendo a la pantalla de asistencia expirada');
        logData(`Entidad: ${data?.entidad}`);
        logData(`Webparam: ${INSURANCE_COMPANIES[data?.entidad].webParam}`);
        navigate(`/${INSURANCE_COMPANIES[data?.entidad].webParam}`);
        break;
      default:
        setLoaded(true);
        break;
    }

    logData(`Y) Manejo de flujo - Estado de grúa: ${craneDataResponse?.estado}`);
    logData(
      // eslint-disable-next-line max-len
      `Y) Manejo de flujo - Paso de grúa: ${craneDataResponse?.estado ? CRANE_LOCATION_STATE_CODES?.[craneDataResponse?.estado] : ''}`,
    );

    setShowMap(!!craneDataResponse?.estado);

    if (
      craneDataResponse?.estado &&
      CRANE_LOCATION_STATE_CODES?.[craneDataResponse?.estado] &&
      CRANE_LOCATION_STATE_CODES?.[craneDataResponse?.estado] > step
    ) {
      const newStep = CRANE_LOCATION_STATE_CODES[craneDataResponse?.estado];
      setStep(newStep);
      if (newStep === 4) {
        navigate(`/completed/${data?.numeroExpediente}/${intervention}`);
        return;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [craneDataResponse, data]);
  // ************************************** */

  // (Y) Verificar si se ha modificado la intervención para recargar la página
  useEffect(() => {
    if (craneDataResponse?.recargarPagina === 1) {
      navigate(`/assistance/${craneDataResponse?.intervencion}/${craneDataResponse?.expediente}`);
    }
  }, [craneDataResponse, navigate]);

  const renderTabs = () => {
    switch (valueTab) {
      case 0:
        return showMap ? (
          <MyCrane craneData={craneDataResponse} coords={coordsMemoized} taxiCoords={taxiCoordsMemoized} />
        ) : (
          <>
            <BodySmallText color={theme.type.secondary} className="m-24px">
              {t('assistance:aia:waiting-for-crane-location')}
            </BodySmallText>
            <img src={images.cars} alt="" />
          </>
        );
      case 1:
        if (data) return <Data values={data} />;
        else return <></>;
      case 2:
        return <Provider />;
      case 3:
        return <Chat isWhatsapp={data?.tipoEnvio == 6} />;
      default:
        break;
    }
  };

  useEffect(() => {
    if (didMountRef.current) return;
    const loadData = async () => {
      await readFromStorage();
    };
    void loadData();
    didMountRef.current = true;
  }, [readFromStorage]);

  useEffect(() => {
    if (!data) return;
    void writeToStorage('interventionData', data);

    const checkAndWriteMetadata = async () => {
      const oldMetadata = await storageService.getItem('metaData');

      if (!oldMetadata) {
        const metaData: IMetadata = {
          interventionNumber: data.intervencion,
          carIdentification: data.matricula,
          name: data.personaContacto,
          assistanceNumber: identifier,
          userIdentification: '',
        };
        await storageService.setItem('metaData', metaData);
      }
    };
    void checkAndWriteMetadata();
  }, [data, writeToStorage, identifier]);

  useEffect(() => {
    if (data?.entidad) {
      dispatch(setProvider({ provider: data?.entidad }));
      storageService.setLocalItem('providerCode', data?.entidad);
    }
  }, [data?.entidad, dispatch]);

  useEffect(() => {
    if (intervention) {
      storageService.setItem('intervention', intervention);
    }
  }, [intervention]);

  if (isError) {
    navigate('/in-our-way');
  }

  useEffect(() => {
    if (!intervention || !identifier) {
      navigate('/', { replace: true });
    }

    const buildChatVariables = async () => {
      const metadata = await storageService.getItem<IMetadata>('metaData');

      const variables = [['pantalla', 'seguimiento']] as ImbeeChatVariables;
      variables.push(
        ['tiempoEstimado', data?.tiempoColaborador ?? 0],
        ['tiempoCalculado', craneDataResponse?.llegadaGrua ?? data?.tiempoEstimado ?? 0],
        ['tareaMedio', data?.tareaMedio ? 1 : 0],
        ['medioAsignado', data?.medio?.medioAsignado ? 1 : 0],
        ['estado', craneDataResponse?.estado],
        ['nombreColaborador', data?.nombreProveedor],
        ['dni', metadata?.userIdentification ?? ''],
        ['matricula', data?.matricula ?? ''],
        ['identificador', identifier ?? ''],
      );
      setChatVariables(variables);
      logData(`SERVICE Variables para el chat establecidas: ${JSON.stringify(variables)}`);
    };
    void buildChatVariables();
  }, [
    intervention,
    identifier,
    navigate,
    data?.tiempoEstimado,
    craneDataResponse?.llegadaGrua,
    data?.tareaMedio,
    data?.medio?.medioAsignado,
    craneDataResponse?.estado,
    data?.tiempoColaborador,
    data?.nombreProveedor,
    data?.matricula,
  ]);

  // seguimiento-grua
  // finalizacion-servicio
  useEffect(() => {
    if (step === 3) {
      pushDataLayerEvent('screen_view', null, 'finalizacion-servicio');
    } else {
      pushDataLayerEvent('screen_view', null, 'seguimiento-grua');
    }

    pushDataLayerEvent('cambio_estado_grua', {
      estado_grua: step,
      color_estado_grua: step === 3 ? 'verde' : 'amarillo',
    });
  }, [pushDataLayerEvent, step]);

  const handleCancelAssistance = useCallback(() => {
    dispatch(showModal({ name: 'cancel-assitance' }));
    pushDataLayerEvent(
      'click_varios',
      {
        tipo_de_click: 'link',
        boton_click: '10a anular servicio',
      },
      'seguimiento-grua',
    );
  }, [dispatch, pushDataLayerEvent]);

  if (!data) return <></>;

  const callPhone = () => {
    try {
      window.open(`tel:${INSURANCE_COMPANIES[data.entidad].phone}`, '_self');
    } catch (error) {
      console.error('Error al intentar realizar la llamada:', error);
      alert('No se pudo iniciar la llamada. Verifica tu configuración de teléfono.');
    }
  };

  return (
    loaded && (
      <>
        <Navbar
          clickRight={() => {
            if (step <= 3) handleCancelAssistance();
          }}
          customRight={
            step <= 3 ? <AlxButton className="ghost danger">{t('common:nav.cancel-service')}</AlxButton> : <></>
          }
          showMenu={showMenu}
          isService={true}
        />
        <ContentLayout className="with-navbar in-assistance">
          <ServiceStyle>
            <Heading4Text className="m-12px m-t-25px" weight={600}>
              {t('assistance:aia.assistance-in-progress.title')} ({data.numeroExpediente})
            </Heading4Text>
            <BodySmallText color={theme.type.secondary} className="m-24px">
              {t('assistance:aia.assistance-in-progress.text')}
            </BodySmallText>
            <div className="content-status">
              <AssistanceNotification
                canShowExpectedTime={!(craneDataResponse?.latitud ?? craneDataResponse?.longitud)}
                expectedTime={arrivalTime}
                deferred={step < 3 && data?.fechaDiferido ? data?.fechaDiferido : undefined}
                disabled={replyData?.codigo === '1'}
                arrived={step === 3}
                onClick={
                  step === 3
                    ? () => {
                        navigate(`/completed/${data.numeroExpediente}/${data.intervencion}`);
                      }
                    : undefined
                }
              />
              {(craneDataResponse?.tareaMedio || craneDataResponse?.medio?.medioAsignado) && (
                <MediaNotification onTheWay={!!craneDataResponse?.medio?.medioAsignado} />
              )}
            </div>
            <div className="content-steps">
              <Stepper activeStep={step} alternativeLabel>
                {steps.map((label, index) => (
                  <Step key={`${label}-${index}`}>
                    <StepLabel slots={{ stepIcon: CustomStepIcon }}>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </div>

            <CustomTabs>
              <Tabs
                value={valueTab}
                indicatorColor="primary"
                textColor="primary"
                onChange={handleChange}
                aria-label="disabled tabs simple"
              >
                <Tab label={t('aia:tabs.my-crane')} className="tab" data-cy="tab-my-crane" />
                <Tab label={t('aia:tabs.data')} className="tab" data-cy="tab-data" />
                <Tab label={t('aia:tabs.provider')} className="tab" data-cy="tab-provider" />
                <Tab label={t('aia:tabs.chat')} className="tab" data-cy="tab-chat" />
              </Tabs>
            </CustomTabs>

            <div className="service-content">{renderTabs()}</div>
          </ServiceStyle>
        </ContentLayout>
        {data.tipoEnvio !== 6 && <ImbeeChat onClick={() => setValueTab(3)} chatVariables={chatVariables} />}
        <ClickToCall
          telefono={INSURANCE_COMPANIES[data.entidad].phone}
          onClick={() => {
            dispatch(showModal({ name: 'assitance-clicktocall' }));
          }}
        />

        {/* Modal Cancel */}
        <ModalContainer
          buttons={[
            {
              className: 'primary',
              onClick: () => {
                if (!identifier) return;
                const assistanceInfo = {
                  insuranceCompany: data?.entidad ?? '',
                  identifier: identifier,
                };
                dispatch(hideModal());
                cancelAssistance.mutate(assistanceInfo, {
                  onSuccess: async () => {
                    await storageService.removeItem('metaData');
                    deleteFromStorage(['interventionData', 'activeAssistance', 'assitanceInformation']);
                    navigate('/cancelled');
                  },
                  onError: () => {
                    // T ODO: handle errors in a better way (toast)
                    console.error('something went wrong');
                  },
                });
              },
              label: t('auth:unregistered.success.cancel-assistance-modal.cancel-assistance-button'),
            },
            {
              className: 'secondary',
              onClick: () => dispatch(hideModal()),
              label: t('auth:unregistered.success.cancel-assistance-modal.cancel-button'),
            },
          ]}
          active={modal.name === 'cancel-assitance'}
          onClose={() => {
            dispatch(hideModal());
          }}
          title={t('auth:unregistered.success.cancel-assistance-modal.title')}
          text={t('auth:unregistered.success.cancel-assistance-modal.description')}
          dataCy="reset-form-modal"
        />
        <ModalContainer
          buttons={[
            {
              className: 'primary',
              onClick: () => {
                // should ask jesus about this
                // resetForms();
                dispatch(hideModal());
                navigate('/');
              },
              label: t('common:buttons.accept'),
            },
          ]}
          active={modal.name === 'assitance-cancel-successful'}
          onClose={() => {
            dispatch(hideModal());
            navigate('/');
          }}
          title={t('auth:unregistered.success.confirmed-assistance-cancelation.title')}
          text={t('auth:unregistered.success.confirmed-assistance-cancelation.description')}
          dataCy="reset-form-modal"
        />
        {/* Modal para confirmación de llamada */}
        <ModalContainer
          buttons={[
            {
              className: 'primary',
              onClick: () => {
                dispatch(hideModal());
                callPhone();
              },
              label: t('assistance:clicktocall.llamar'),
            },
            {
              className: 'primary',
              onClick: () => {
                dispatch(hideModal());
              },
              label: t('assistance:clicktocall.no'),
            },
          ]}
          active={modal.name === 'assitance-clicktocall'}
          onClose={() => {
            dispatch(hideModal());
          }}
          title={t('assistance:clicktocall.title')}
          text={t('assistance:clicktocall.description', { telefono: INSURANCE_COMPANIES[data.entidad].phone })}
        />
      </>
    )
  );
};

export default Service;
